/**
 * <Icon> — RTL-aware Lucide wrapper.
 *
 * Responsibilities (UI-SPEC §RTL & Accessibility, DS-04, Pitfall H7):
 *   1. Accept any Lucide icon component via the `as` prop. This is the SINGLE
 *      IMPORT SITE for Lucide glyphs in the design system — every consumer
 *      goes through here so tree-shaking stays effective and we never import
 *      the full barrel.
 *   2. Determine the icon's kebab-case Lucide id — from the explicit `name`
 *      prop, else auto-derived from the Lucide component's `displayName` — and
 *      look it up in `DIRECTIONAL_ICONS`. If it matches, apply `rtl:-scale-x-100`
 *      so the glyph mirrors horizontally when `<html dir="rtl">`. If it does not
 *      match, render as-is — logos, calendar, search, bell, settings never flip.
 *      Auto-derivation means every directional Lucide glyph flips in RTL even
 *      when a call site forgets the `name` prop; pass `noFlip` to opt a specific
 *      site out.
 *   3. Accessibility: if the consumer does not supply an `aria-label` (or
 *      passes an empty one), the icon is decorative → `aria-hidden="true"`.
 *      If a label is supplied, we drop `aria-hidden` and treat the icon as
 *      an independent graphical control (`role="img"` on the wrapper `<span>`).
 *
 * Sizing is forwarded to Lucide's `size` prop (numeric pixel value).
 *
 * Example:
 *   import { ChevronRight } from "lucide-react";
 *   <Icon as={ChevronRight} name="chevron-right" size={20} />
 */
import React from "react";
import type { LucideProps } from "lucide-react";
import { cn } from "../../utils";
import { DIRECTIONAL_ICONS } from "./directionalIcons";

type LucideIconComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

/**
 * Lucide sets `Component.displayName` to the PascalCase icon name (e.g.
 * `"ChevronRight"`). Convert it to the kebab-case id the allow-list uses, so a
 * call site that omits the `name` prop still flips directional glyphs in RTL.
 */
function toKebabName(pascal: string): string {
  return pascal
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([a-zA-Z])([0-9])/g, "$1-$2")
    .toLowerCase();
}

export interface IconProps {
  /** The Lucide icon component (e.g. `ChevronRight`, `Bell`, `Search`). */
  as: LucideIconComponent;
  /**
   * Kebab-case Lucide name, used ONLY for the directional allow-list lookup
   * (e.g. `"chevron-right"`). Optional: when omitted it's auto-derived from the
   * Lucide component's `displayName`, so directional glyphs still flip in RTL.
   * Pass it explicitly only to override the derived name.
   */
  name?: string;
  /** Pixel size (width + height). Defaults to 20 (matches design-system scale). */
  size?: number;
  /** Force the icon NOT to flip even if `name` is in DIRECTIONAL_ICONS. */
  noFlip?: boolean;
  /** Accessible name. When absent/empty, the icon is decorative (aria-hidden). */
  "aria-label"?: string;
  /** Additional classes merged via `cn()`. */
  className?: string;
  /** Color — Lucide forwards to `stroke`. Use `currentColor` (default) + Tailwind text classes. */
  color?: string;
  /** Stroke width forwarded to Lucide (default 2). */
  strokeWidth?: number;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  {
    as: Component,
    name,
    size = 20,
    noFlip = false,
    "aria-label": ariaLabel,
    className,
    color,
    strokeWidth,
  },
  ref,
) {
  const resolvedName =
    name ?? toKebabName((Component as { displayName?: string }).displayName ?? "");
  const shouldFlip = !noFlip && resolvedName.length > 0 && DIRECTIONAL_ICONS.has(resolvedName);
  const isDecorative = !ariaLabel || ariaLabel.length === 0;

  // Lucide's `aria-hidden` + `role` props flow through to the rendered <svg>.
  // We let Lucide own the <svg> element so no extra DOM is injected.
  const a11y = isDecorative
    ? { "aria-hidden": true as const, focusable: false as const }
    : { "aria-label": ariaLabel, role: "img" as const };

  return (
    <Component
      ref={ref}
      size={size}
      {...(color !== undefined ? { color } : {})}
      {...(strokeWidth !== undefined ? { strokeWidth } : {})}
      className={cn(shouldFlip && "rtl:-scale-x-100", className)}
      {...a11y}
    />
  );
});
